import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TripComponent, InsertTripComponent } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";

// Hook to fetch all components for a trip
export function useTripComponents(tripId: number, enabled = true) {
  const queryClient = useQueryClient();
  
  // Query for fetching components
  const componentsQuery = useQuery({
    queryKey: ['/api/trips', tripId, 'components'],
    queryFn: async () => {
      const response = await fetch(`/api/trips/${tripId}/components`);
      if (!response.ok) {
        throw new Error('Failed to fetch trip components');
      }
      return response.json() as Promise<TripComponent[]>;
    },
    enabled: !!tripId && enabled,
  });
  
  // Mutation for creating a component
  const createComponentMutation = useMutation({
    mutationFn: async (component: Omit<InsertTripComponent, 'id'>) => {
      // Fix parameter order: method, url, data
      return apiRequest('POST', '/api/components', component) as Promise<TripComponent>;
    },
    onSuccess: () => {
      // Invalidate the components query to refetch the data
      queryClient.invalidateQueries({ queryKey: ['/api/trips', tripId, 'components'] });
    },
  });

  // Function to create multiple components at once
  const createComponents = async (components: Omit<InsertTripComponent, 'id'>[]) => {
    try {
      // Process components sequentially to ensure proper order
      for (const component of components) {
        await createComponentMutation.mutateAsync(component);
      }
      return true;
    } catch (error) {
      console.error("Error creating components:", error);
      throw error;
    }
  };

  return {
    components: componentsQuery.data || [],
    isLoading: componentsQuery.isLoading,
    error: componentsQuery.error,
    createComponent: createComponentMutation.mutate,
    createComponents,
    isCreating: createComponentMutation.isPending,
  };
}